import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { mapStyleService } from './MapStyleService';
import { solidColorService } from './solid-color-service';
import type { MapStyleType, MapStyleMetadata } from './map-styles-config';
import './map-style-selector.css';

/**
 * Colores predefinidos para el color picker
 */
const COLOR_PRESETS = [
  '#ffffff', '#cbd5e1', '#94a3b8', '#64748b',
  '#334155', '#0f172a', '#6366f1', '#3b82f6',
  '#06b6d4', '#14b8a6', '#22c55e', '#84cc16',
  '#f59e0b', '#f97316', '#ef4444', '#ec4899',
  '#8b5cf6', '#a855f7', '#78716c', '#0ea5e9',
  '#d946ef', '#f43f5e', '#10b981', '#eab308',
];

/**
 * ColorPicker - Componente de selección de color inline
 */
function ColorPicker({
  currentColor,
  onSelect,
}: {
  currentColor: string;
  onSelect: (hex: string) => void;
}): JSX.Element {
  return (
    <div className="map-style-color-picker">
      <div className="map-style-color-grid">
        {COLOR_PRESETS.map((hex) => (
          <button
            key={hex}
            type="button"
            className={`map-style-color-preset${hex === currentColor ? ' active' : ''}`}
            style={{ backgroundColor: hex }}
            onClick={() => onSelect(hex)}
            title={hex}
            aria-label={`Seleccionar color ${hex}`}
          />
        ))}
      </div>
      <div className="map-style-color-info">
        <span className="map-style-color-label">Color actual:</span>
        <span
          className="map-style-color-current"
          style={{ backgroundColor: currentColor }}
        />
        <span className="map-style-color-hex">{currentColor}</span>
      </div>
    </div>
  );
}

/**
 * Genera un SVG placeholder para miniaturas de estilos
 */
function generatePlaceholderSVG(styleType: MapStyleType): string {
  const colors: Record<MapStyleType, { bg: string; accent: string; pattern: string }> = {
    dark: { bg: '#0E2A38', accent: '#1A3D52', pattern: '#2FA4B5' },
    light: { bg: '#E8EEF2', accent: '#D4DCE3', pattern: '#2FA4B5' },
    streets: { bg: '#F5F5F0', accent: '#E0DFD8', pattern: '#FF8C42' },
    terrain: { bg: '#8B7355', accent: '#A68A64', pattern: '#5D8A3A' },
    satellite: { bg: '#1A3D2E', accent: '#2D5F47', pattern: '#4A7C59' },
    solid: { bg: solidColorService.getColor(), accent: solidColorService.getColor(), pattern: solidColorService.getColor() },
  };

  const { bg, accent, pattern } = colors[styleType];

  return `data:image/svg+xml,${encodeURIComponent(`
    <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="grad-${styleType}" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:${bg};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${accent};stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="400" height="300" fill="url(#grad-${styleType})"/>
      <circle cx="200" cy="150" r="40" fill="${pattern}" opacity="0.3"/>
      <path d="M 50,150 Q 200,50 350,150" stroke="${pattern}" stroke-width="2" fill="none" opacity="0.5"/>
      <path d="M 50,200 Q 200,100 350,200" stroke="${pattern}" stroke-width="2" fill="none" opacity="0.3"/>
    </svg>
  `)}`;
}

/**
 * Props para MapStyleSelector
 */
interface MapStyleSelectorProps {
  /**
   * Instancia del mapa MapLibre
   */
  map: MapLibreMap | null;

  /**
   * Si el modal está abierto
   */
  isOpen: boolean;

  /**
   * Callback para cerrar el modal
   */
  onClose: () => void;

  /**
   * Callback cuando cambia el estilo
   */
  onStyleChange?: (styleType: MapStyleType) => void;
}

/**
 * MapStyleSelector - Modal para seleccionar estilos de mapa
 *
 * @version 1.0
 * @module core/map-engine
 *
 * @description
 * Modal compacto que muestra los 5 estilos de mapa disponibles.
 * Permite cambiar el estilo del mapa con preview visual.
 *
 * @remarks
 * **Características**:
 * - Modal con glassmorphism intenso
 * - Grid de 5 estilos con iconos y descripciones
 * - Preview de color representativo
 * - Indica estilo actual seleccionado
 * - Filtra estilos no disponibles (sin API key)
 * - Accesible (ESC para cerrar, keyboard navigation)
 *
 * **Estilos disponibles**:
 * 1. Dark (oscuro profesional)
 * 2. Light (claro minimalista)
 * 3. Streets (vial detallado OSM)
 * 4. Terrain (relieve y topografía)
 * 5. Satellite (imagen satelital)
 *
 * @example
 * ```tsx
 * import { MapStyleSelector } from '@/core/map-engine';
 *
 * function MapView() {
 *   const [map, setMap] = useState<MapLibreMap | null>(null);
 *   const [showSelector, setShowSelector] = useState(false);
 *
 *   return (
 *     <>
 *       <button onClick={() => setShowSelector(true)}>
 *         Cambiar estilo
 *       </button>
 *
 *       <MapStyleSelector
 *         map={map}
 *         isOpen={showSelector}
 *         onClose={() => setShowSelector(false)}
 *       />
 *     </>
 *   );
 * }
 * ```
 */
export function MapStyleSelector({
  map,
  isOpen,
  onClose,
  onStyleChange,
}: MapStyleSelectorProps): JSX.Element | null {
  const [selectedStyle, setSelectedStyle] = useState<MapStyleType>(
    mapStyleService.getCurrentStyle()
  );
  const [availableStyles, setAvailableStyles] = useState<MapStyleMetadata[]>([]);
  const [solidColor, setSolidColor] = useState<string>(solidColorService.getColor());

  /**
   * Cargar estilos disponibles al montar
   */
  useEffect(() => {
    const styles = mapStyleService.getAvailableStyles();
    setAvailableStyles(styles);
  }, []);

  /**
   * Cerrar modal con tecla ESC
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  /**
   * Manejar selección de estilo
   */
  const handleSelectStyle = (styleType: MapStyleType): void => {
    if (!map) return;

    // Actualizar estilo en el servicio
    mapStyleService.setCurrentStyle(styleType);

    // Cambiar estilo en el mapa
    const style = mapStyleService.getStyle(styleType);
    map.setStyle(style as string);

    // Actualizar estado local
    setSelectedStyle(styleType);

    // Callback externo
    onStyleChange?.(styleType);

    // NO cerrar modal si es estilo solid (para permitir cambiar color)
    if (styleType !== 'solid') {
      onClose();
    }
  };

  /**
   * Manejar cambio de color para estilo solid
   */
  const handleColorChange = (color: string): void => {
    if (!map) return;

    // Actualizar color en el servicio
    solidColorService.setColor(color);

    // Actualizar estado local
    setSolidColor(color);

    // Si el estilo actual es solid, regenerar y aplicar
    if (selectedStyle === 'solid') {
      const style = mapStyleService.getStyle('solid');
      map.setStyle(style as object);
    }
  };

  /**
   * Manejar click en overlay (cerrar)
   */
  const handleOverlayClick = (event: React.MouseEvent): void => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // No renderizar si no está abierto
  if (!isOpen) return null;

  // Renderizar en un portal para estar fuera de la jerarquía DOM
  // Esto garantiza que el z-index funcione correctamente
  return createPortal(
    <div
      className="map-style-selector-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="style-selector-title"
    >
      <div className="map-style-selector-modal">
        {/* Header */}
        <div className="map-style-selector-header">
          <h3 id="style-selector-title" className="map-style-selector-title">
            Estilo de mapa
          </h3>
          <button
            className="map-style-selector-close"
            onClick={onClose}
            aria-label="Cerrar selector de estilos"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="3" y1="3" x2="13" y2="13" />
              <line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>
        </div>

        {/* Grid de estilos */}
        <div className="map-style-selector-grid">
          {availableStyles.map((style) => (
            <button
              key={style.id}
              className={`map-style-card ${selectedStyle === style.id ? 'active' : ''}`}
              onClick={() => handleSelectStyle(style.id)}
              aria-label={`Seleccionar estilo ${style.name}`}
            >
              {/* Miniatura del estilo */}
              <div className="map-style-card-preview">
                <img
                  src={style.thumbnailUrl}
                  alt={`Preview del estilo ${style.name}`}
                  className="map-style-card-thumbnail"
                  loading="lazy"
                  onError={(e) => {
                    // Fallback si la imagen no carga: usar SVG placeholder
                    const target = e.target as HTMLImageElement;
                    target.src = generatePlaceholderSVG(style.id);
                  }}
                />
              </div>

              {/* Info */}
              <div className="map-style-card-info">
                <h4 className="map-style-card-name">{style.name}</h4>
                <p className="map-style-card-description">{style.description}</p>
              </div>

              {/* Selected indicator */}
              {selectedStyle === style.id && (
                <div className="map-style-card-selected">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3,8 6,11 13,4" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Color Picker - Solo visible cuando se selecciona estilo solid */}
        {selectedStyle === 'solid' && (
          <div className="map-style-solid-section">
            <div className="map-style-solid-header">
              <h4 className="map-style-solid-title">Configurar color de fondo</h4>
              <p className="map-style-solid-description">
                Selecciona el color del fondo liso
              </p>
            </div>
            <ColorPicker
              currentColor={solidColor}
              onSelect={handleColorChange}
            />
          </div>
        )}

        {/* Footer con info */}
        {!mapStyleService.hasApiKey() && (
          <div className="map-style-selector-footer">
            <p className="map-style-selector-info">
              💡 Algunos estilos requieren API key de MapTiler para estar disponibles.
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
